import { useEffect, useState } from "react";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { recordPricingRequest, unwrapData } from "layouts/record-pricing/api";

const formatPrice = (value) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(value);

function PricingTierAssignment({ userId }) {
  const [tiers, setTiers] = useState([]);
  const [tierId, setTierId] = useState("");
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [message, setMessage] = useState(null);
  const sessionId = useSelector((state) => state.login.sessionId);
  const navigate = useNavigate();

  const handleError = (error) => {
    if (error.status === 401 || error.status === 403) {
      navigate("/authentication/sign-in");
      return;
    }

    setMessage({ color: "error", text: error.message || "The pricing request failed." });
  };

  useEffect(() => {
    let active = true;

    async function loadTiers() {
      setLoading(true);
      setMessage(null);
      try {
        const response = await recordPricingRequest("admin/tiers", sessionId);
        const data = unwrapData(response);
        const availableTiers = Array.isArray(data) ? data : data?.tiers || [];

        if (active) {
          setTiers(availableTiers);
          setTierId((current) => current || String(availableTiers[0]?.id || ""));
        }
      } catch (error) {
        if (active) handleError(error);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadTiers();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const assignTier = async () => {
    if (!userId || !tierId) return;

    setAssigning(true);
    setMessage(null);
    try {
      await recordPricingRequest(`admin/users/${userId}/tiers/${tierId}`, sessionId, {
        method: "PUT",
      });
      const selectedTier = tiers.find((tier) => String(tier.id) === String(tierId));
      setMessage({ color: "success", text: `${selectedTier?.name || "Tier"} assigned.` });
    } catch (error) {
      handleError(error);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Card sx={{ boxShadow: "none", height: "100%" }}>
      <MDBox p={2}>
        <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
          pricing tier
        </MDTypography>
      </MDBox>
      <MDBox pt={1} pb={2} px={2}>
        <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
          record pricing
        </MDTypography>
        <MDTypography display="block" variant="button" color="text" mt={1} mb={2}>
          Assign a per-record pricing tier to this user.
        </MDTypography>

        {loading ? (
          <MDBox display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} color="info" />
          </MDBox>
        ) : (
          <>
            <MDTypography display="block" variant="caption" color="text" mb={0.75}>
              Available tier
            </MDTypography>
            <MDInput
              select
              value={tierId}
              onChange={(event) => {
                setTierId(event.target.value);
                setMessage(null);
              }}
              inputProps={{ "aria-label": "Pricing tier" }}
              disabled={tiers.length === 0}
              fullWidth
            >
              {tiers.map((tier) => (
                <MenuItem key={tier.id} value={String(tier.id)}>
                  {tier.name} ({formatPrice(tier.pricePerRecord)})
                </MenuItem>
              ))}
            </MDInput>
            <MDButton
              color="info"
              fullWidth
              sx={{ mt: 2 }}
              disabled={!tierId || assigning}
              onClick={assignTier}
            >
              <PersonAddIcon fontSize="small" sx={{ mr: 0.75 }} />
              {assigning ? "Assigning..." : "Assign tier"}
            </MDButton>
          </>
        )}

        {!loading && tiers.length === 0 && (
          <MDTypography display="block" variant="caption" color="text" mt={1}>
            No pricing tiers are available.
          </MDTypography>
        )}
        {message && (
          <MDTypography display="block" variant="caption" color={message.color} mt={1.5}>
            {message.text}
          </MDTypography>
        )}
      </MDBox>
    </Card>
  );
}

PricingTierAssignment.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

PricingTierAssignment.defaultProps = {
  userId: 0,
};

export default PricingTierAssignment;